import { useLayoutEffect, useRef } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { ASSISTANT_FIELD_LABELS, ASSISTANT_INCOME_VALUE_FIELDS } from '@brighte/validation';
import type { AssistantFormFieldInput } from '@graphql/generated/graphql';
import { useAssistantSafe } from './AssistantProvider';

type FormField = string | { fieldName: string; sourceName: string; isEmpty?: () => boolean };
const VALUE_FIELDS = new Set(ASSISTANT_INCOME_VALUE_FIELDS);

function isFilled(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'boolean';
}

function RegisteredFormFields({
  fields,
  register,
}: {
  fields: readonly FormField[];
  register: (reader: () => AssistantFormFieldInput[]) => () => void;
}) {
  const form = useFormContext();
  const formState = useFormState({ control: form.control });
  // Subscribe so getFieldState sees dirty-state changes as well as fresh values.
  const dirtyFields = formState.dirtyFields;
  // Subscribe to errors too, so a field that starts erroring re-runs the reader.
  const errors = formState.errors;
  const reader = useRef<() => AssistantFormFieldInput[]>(() => []);
  reader.current = () =>
    fields.flatMap(field => {
      const fieldName = typeof field === 'string' ? field : field.fieldName;
      const sourceName = typeof field === 'string' ? field : field.sourceName;
      if (!Object.hasOwn(ASSISTANT_FIELD_LABELS, fieldName)) return [];
      const value = form.getValues(sourceName);
      const filled = isFilled(value) && !(typeof field !== 'string' && field.isEmpty?.());
      const fieldState = form.getFieldState(sourceName, { ...formState, dirtyFields, errors });
      const entry: AssistantFormFieldInput = {
        fieldName,
        filled,
        changed: fieldState.isDirty,
      };
      // Sent only when true. "Filled" cannot stand in for this: a field can hold a value and
      // still be rejected (a malformed phone number), and that is exactly the case where the
      // assistant should point at this field rather than the next empty one.
      if (fieldState.invalid) entry.invalid = true;
      if (
        filled &&
        VALUE_FIELDS.has(fieldName) &&
        (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
      ) {
        const serialized = String(value);
        if (serialized.length <= 64) entry.value = serialized;
      }
      return [entry];
    });
  useLayoutEffect(() => register(() => reader.current()), [register]);
  return null;
}

export function AssistantFormFields({ fields }: { fields: readonly FormField[] }) {
  const assistant = useAssistantSafe();
  if (!assistant?.isEnabledOnStep) return null;
  return <RegisteredFormFields fields={fields} register={assistant.registerFormReader} />;
}
